import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import './booking/booking-shared.css';
import './Branding.css';

const Branding = () => {
  const { user, can } = useAuth();
  const { updateBranding } = useBranding();
  const [branding, setBranding] = useState({
    primaryColor: '#2d2d2d',
    secondaryColor: '#7c35e8',
    accentColor: '#9b4dca',
    backgroundColor: '#eaeaec',
    textColor: '#1a1a1a',
    logoUrl: '',
    fontFamily: "'Exo 2', Arial, sans-serif",
    website: '',
    description: ''
  });
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBranding();
    fetchPresets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBranding = async () => {
    try {
      const response = await axios.get('/api/branding');
      const brandingData = { ...branding, ...response.data };
      setBranding(brandingData);
      updateBranding(brandingData);
    } catch (err) {
      console.error('Failed to fetch branding:', err);
      setError('Failed to load branding settings');
    }
  };

  const fetchPresets = async () => {
    try {
      const response = await axios.get('/api/branding/presets');
      setPresets(response.data);
    } catch (err) {
      console.error('Failed to fetch presets:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newBranding = { ...branding, [name]: value };
    setBranding(newBranding);
    updateBranding(newBranding);
  };

  const handleColorChange = (colorName, value) => {
    const newBranding = { ...branding, [colorName]: value };
    setBranding(newBranding);
    updateBranding(newBranding);
  };

  const applyPreset = (preset) => {
    const newBranding = {
      ...branding,
      primaryColor: preset.primaryColor,
      secondaryColor: preset.secondaryColor,
      accentColor: preset.accentColor,
      backgroundColor: preset.backgroundColor,
      textColor: preset.textColor
    };
    setBranding(newBranding);
    updateBranding(newBranding);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Only send the fields that are allowed by the backend validation
      const updateData = {
        primaryColor: branding.primaryColor || null,
        secondaryColor: branding.secondaryColor || null,
        accentColor: branding.accentColor || null,
        backgroundColor: branding.backgroundColor || null,
        textColor: branding.textColor || null,
        logoUrl: branding.logoUrl || '',
        fontFamily: branding.fontFamily || '',
        website: branding.website || '',
        description: branding.description || ''
      };

      const response = await axios.put('/api/branding', updateData);
      const updatedBranding = { ...branding, ...response.data };
      setBranding(updatedBranding);
      updateBranding(updatedBranding);
      setSuccess('Branding settings updated successfully!');
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      console.error('Failed to update branding:', err);
      setError(err.response?.data?.error || 'Failed to update branding settings');
    } finally {
      setLoading(false);
    }
  };

  const resetToDefaults = () => {
    const newBranding = {
      ...branding,
      primaryColor: '#2c3e50',
      secondaryColor: '#3498db',
      accentColor: '#d4af37',
      backgroundColor: '#f8f9fa',
      textColor: '#212529',
      fontFamily: '',
    };
    setBranding(newBranding);
    updateBranding(newBranding);
  };

  if (!user || !can('settings', 'MANAGE')) {
    return (
      <div className="bk-page bk-page--md">
        <h2>Club branding</h2>
        <p className="bk-muted">You need Manage access to settings to change the club's branding.</p>
      </div>
    );
  }

  return (
    <div className="bk-page brand">
      <header className="brand__intro">
        <h2>Club branding</h2>
        <p className="bk-muted">Your club's colours, logo and font. Changes show straight away; press Save to keep them.</p>
      </header>

      {success && <div className="brand__notice brand__notice--ok" role="status">{success}</div>}
      {error && <div className="brand__notice brand__notice--error" role="alert">{error}</div>}

      <div className="brand__layout">
        <form className="brand__form" onSubmit={handleSubmit}>
          <section className="bk-card">
            <h3 className="brand__heading">Colours</h3>
            {presets.length > 0 && (
              <div className="brand__presets" aria-label="Colour presets">
                {presets.map(preset => (
                  <button key={preset.name} type="button" className="brand__preset" onClick={() => applyPreset(preset)}>
                    <span className="brand__dots" aria-hidden="true">
                      {[preset.primaryColor, preset.secondaryColor, preset.accentColor].map((c, i) => (
                        <span key={i} className="brand__dot" style={{ backgroundColor: c }} />
                      ))}
                    </span>
                    {preset.name}
                  </button>
                ))}
              </div>
            )}
            <div className="brand__colours">
              {COLOUR_FIELDS.map(({ key, label, fallback }) => (
                <div key={key} className="brand__colour">
                  <label htmlFor={`brand-${key}`}>{label}</label>
                  <div className="brand__colour-input">
                    <input
                      type="color"
                      aria-label={`${label} picker`}
                      value={branding[key] || fallback}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                    />
                    <input
                      id={`brand-${key}`}
                      type="text"
                      className="bk-input"
                      value={branding[key] || ''}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      placeholder={fallback}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bk-card">
            <h3 className="brand__heading">Logo and font</h3>
            <div className="brand__fields">
              <label className="brand__field">
                Logo URL
                <input
                  type="url"
                  className="bk-input"
                  name="logoUrl"
                  value={branding.logoUrl || ''}
                  onChange={handleInputChange}
                  placeholder="https://example.com/logo.png"
                />
              </label>
              <label className="brand__field">
                Font
                <select
                  className="bk-input"
                  name="fontFamily"
                  value={branding.fontFamily || ''}
                  onChange={handleInputChange}
                >
                  <option value="">Default (System)</option>
                  {FONTS.map(([value, name]) => <option key={value} value={value}>{name}</option>)}
                </select>
              </label>
            </div>
          </section>

          <section className="bk-card">
            <h3 className="brand__heading">Club information</h3>
            <label className="brand__field">
              Website
              <input
                type="url"
                className="bk-input"
                name="website"
                value={branding.website || ''}
                onChange={handleInputChange}
                placeholder="https://yourclub.com"
              />
            </label>
            <label className="brand__field">
              Description
              <textarea
                className="bk-input"
                name="description"
                value={branding.description || ''}
                onChange={handleInputChange}
                rows="3"
                placeholder="A short description of your club"
                maxLength="500"
              />
              <span className="bk-muted brand__count">{branding.description?.length || 0}/500</span>
            </label>
          </section>

          <div className="brand__actions">
            <button type="button" className="bk-btn bk-btn--outline" onClick={resetToDefaults}>
              Reset colours and font
            </button>
            <button type="submit" className="bk-btn bk-btn--primary" disabled={loading}>
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>

        <aside className="brand__aside">
          <div className="bk-card">
            <h3 className="brand__heading">Preview</h3>
            <div
              className="brand-preview"
              style={{
                backgroundColor: branding.backgroundColor,
                color: branding.textColor,
                fontFamily: branding.fontFamily || 'inherit',
              }}
            >
              <div className="brand-preview__header" style={{ backgroundColor: branding.primaryColor }}>
                {branding.logoUrl
                  ? <img src={branding.logoUrl} alt="Logo" />
                  : <span className="brand-preview__logo">Your club logo</span>}
                <span className="brand-preview__nav">
                  <span>Home</span>
                  <span>Bookings</span>
                </span>
              </div>
              <div className="brand-preview__content">
                <div className="brand-preview__card">
                  <div className="brand-preview__card-title" style={{ backgroundColor: branding.secondaryColor }}>
                    Sample card
                  </div>
                  <div className="brand-preview__card-body">
                    <p>This is how your content looks with these colours.</p>
                    <span className="brand-preview__button" style={{ backgroundColor: branding.accentColor }}>
                      Action button
                    </span>
                  </div>
                </div>
                <p className="brand-preview__heading" style={{ color: branding.primaryColor }}>Heading text</p>
                <p>Regular text in your font and colours.</p>
                <p className="brand-preview__small">Secondary text</p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

const COLOUR_FIELDS = [
  { key: 'primaryColor', label: 'Primary', fallback: '#2c3e50' },
  { key: 'secondaryColor', label: 'Secondary', fallback: '#3498db' },
  { key: 'accentColor', label: 'Accent', fallback: '#d4af37' },
  { key: 'backgroundColor', label: 'Background', fallback: '#f8f9fa' },
  { key: 'textColor', label: 'Text', fallback: '#212529' },
];

const FONTS = [
  ["'Lilita One', sans-serif", 'Lilita One (Bold & Playful)'],
  ['Arial, sans-serif', 'Arial (Clean & Simple)'],
  ['Helvetica, sans-serif', 'Helvetica (Modern)'],
  ["'Open Sans', sans-serif", 'Open Sans (Friendly)'],
  ["'Roboto', sans-serif", 'Roboto (Professional)'],
  ["'Montserrat', sans-serif", 'Montserrat (Elegant)'],
  ["'Poppins', sans-serif", 'Poppins (Rounded)'],
  ['Georgia, serif', 'Georgia (Classic Serif)'],
  ["'Times New Roman', serif", 'Times New Roman (Traditional)'],
  ["'Playfair Display', serif", 'Playfair Display (Elegant Serif)'],
  ["'Source Code Pro', monospace", 'Source Code Pro (Monospace)'],
  ["'Courier New', monospace", 'Courier New (Typewriter)'],
  ['Verdana, sans-serif', 'Verdana (Web Safe)'],
  ["'Trebuchet MS', sans-serif", 'Trebuchet MS (Humanist)'],
];

export default Branding;
